/* eslint-disable security/detect-object-injection */
/*--------------------------------------------------------------
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * See License.txt in the project root for license information.
 *--------------------------------------------------------------*/

/* eslint-disable no-duplicate-imports */
import { ICoreContext, msdyn365Commerce } from '@msdyn365-commerce/core-internal';
import { AttributeValue, ProductDeliveryOptions, ProductPrice, ReleasedProductType, SimpleProduct } from '@msdyn365-commerce/retail-proxy';
import { ICartState } from '@msdyn365-commerce/global-state';
import { BulkPurchase } from '@msdyn365-commerce-modules/bulk-purchase';
import {
    ArrayExtensions,
    DeliveryMode,
    FinitePromiseQueue,
    FinitePromiseQueueError,
    getDeliveryOptionsForSelectedVariant,
    GetDeliveryOptionsForSelectedVariantInput,
    getDimensionsForSelectedVariant,
    GetDimensionsForSelectedVariantInput,
    getInventoryLevelCodeFromDimensionValue,
    getPriceForSelectedVariant,
    getProductAvailabilitiesForSelectedVariant,
    getSelectedVariant,
    IDimensionsApp,
    IDimensionValueForSelectedVariant,
    InventoryLevelValues,
    IProductInventoryInformation,
    IPromiseQueue,
    PriceForSelectedVariantInput,
    ProductAvailabilitiesForSelectedVariantInput,
    SelectedVariantInput,
    setDimensionValuesToQuery
} from '@msdyn365-commerce-modules/retail-actions';
import { Button, getTelemetryObject, IModuleProps, INodeProps, ITelemetryContent, format } from '@msdyn365-commerce-modules/utilities';
import classnames from 'classnames';
import * as React from 'react';

import {
    getBuyboxAddToCart,
    getBuyBoxInventoryLabel,
    getBuyboxKeyInPrice,
    getBuyboxProductAddToOrderTemplate,
    getBuyboxProductAddToWishlist,
    getBuyboxProductDescription,
    getBuyboxProductPrice,
    getBuyboxProductQuantity,
    getBuyboxProductRating,
    getBuyboxProductTitle,
    getBuyboxProductUnitOfMeasure,
    getBuyboxShopSimilarButton,
    getQuantityLimitsMessages,
    RetailDefaultOrderQuantityLimitsFeatureName
} from './components/buybox-components';
// import { getBuyboxProductConfigure } from '@msdyn365-commerce-modules/buybox';
import { getBuyboxProductConfigure } from './components/lds-buybox-product-configure';
import {
    IBuyboxAddToCartViewProps,
    IBuyboxAddToOrderTemplateViewProps,
    IBuyboxAddToWishlistViewProps,
    IBuyboxCallbacks,
    IBuyboxCommonData,
    IBuyboxExtentedProps,
    IBuyboxKeyInPriceViewProps,
    IBuyboxProductConfigureViewProps,
    IBuyboxProductQuantityViewProps,
    IBuyboxShopSimilarLookViewProps,
    IBuyboxState,
    IErrorState,
    ShopSimiliarButtonType
} from '@msdyn365-commerce-modules/buybox';
import { ILdsBuyboxData } from './lds-buybox.data';
import { ILdsBuyboxProps, ILdsBuyboxResources } from './lds-buybox.props.autogenerated';
import { getBuyboxFindInStore, IBuyboxFindInStoreViewProps } from './components/lds-buybox-find-in-store';
// import { churchRetailChannelCountryAsyncAsync } from '../../actions/DataActionExtension.g';
import { IChurchRetailChannelCountryEntity } from '../../actions/DataServiceEntities.g';
import { updateCartLineAttributeValues } from '../../helpers/utilities/cart-line-utilities';
// @ts-ignore
import { Error } from '@churchofjesuschrist/eden-alert';

interface IBuyboxStateExtended extends IBuyboxState {
    subscriptionAccountNumber: string | undefined;
    showAddToCart: boolean;
    showFindStore: boolean;
    localCurrency: boolean;
    countryRegion: string;
    active: boolean;
    pickupActive: boolean;
    updatedDimensions: any;
    quantityError: boolean;
    quantityErrorMessage: string | undefined;
}

export interface IBuyboxCallbacksExtended extends IBuyboxCallbacks {
    addSubscriptionAccountNumberAttribute: (product: SimpleProduct, cart: ICartState) => void;
}
export interface IBuyboxViewProps extends ILdsBuyboxProps<ILdsBuyboxData> {
    resources: ILdsBuyboxResources;
    state: IBuyboxStateExtended;
    ModuleProps: IModuleProps;
    ProductInfoContainerProps: INodeProps;
    MediaGalleryContainerProps: INodeProps;
    callbacks: IBuyboxCallbacksExtended;
    mediaGallery?: React.ReactNode;
    title?: React.ReactNode;
    description?: React.ReactNode;
    rating?: React.ReactNode;
    price?: React.ReactNode;
    bulkPurchaseLink?: React.ReactNode;
    addToOrderTemplate?: IBuyboxAddToOrderTemplateViewProps;
    addToWishlist?: IBuyboxAddToWishlistViewProps;
    max: number | undefined;
    addToCart: IBuyboxAddToCartViewProps | null;
    findInStore?: IBuyboxFindInStoreViewProps | null;
    quantity?: IBuyboxProductQuantityViewProps;
    configure?: IBuyboxProductConfigureViewProps;
    inventoryLabel?: React.ReactNode;
    shopSimilarLook?: IBuyboxShopSimilarLookViewProps;
    shopSimilarDescription?: IBuyboxShopSimilarLookViewProps;
    quantityLimitsMessages: React.ReactNode;
    telemetryContent?: ITelemetryContent;
    keyInPrice?: IBuyboxKeyInPriceViewProps;
    unitOfMeasure?: React.ReactNode;
    ProductSpecification: IModuleProps;
    ProductSpecificationTableProps: INodeProps;
    ProductSpecificationTableBodyProps: INodeProps;
    productSpecificationResult?: AttributeValue[];
    churchRetailChannelCountry?: IChurchRetailChannelCountryEntity[];
    isSubscription: boolean;
    isManualSubscription: boolean;
    setSubscriptionAccountNumber: (subAccNumber: string) => void;
    buyboxQuantityError: React.ReactNode;
}

/**
 * Buybox Module.
 */
class Buybox extends React.PureComponent<ILdsBuyboxProps<ILdsBuyboxData>, IBuyboxStateExtended> {
    /**
     * A queue of tasks of processing the changes in the dimensions.
     * Limit to two processes:
     * 1 - for the current process, which is under execution at the moment.
     * 2 - next process, which will process the latest version of data.
     * @remark Enqueueing new promises will discard the previous ones (except the one which is under processing).
     */
    private readonly dimensionUpdateQueue: IPromiseQueue<void> = new FinitePromiseQueue<void>(2);

    private dimensions: { [id: number]: string } = {};
    private initializedDimensions: Set<string> = new Set();

    private readonly buyboxCallbacks: IBuyboxCallbacksExtended = {
        updateQuantity: (newQuantity: number): boolean => {
            const errorState = { ...this.state.errorState };
            // errorState.quantityError = undefined;
            errorState.otherError = undefined;
            this.setState({ quantity: newQuantity, errorState });
            return true;
        },
        updateErrorState: (newErrorState: IErrorState): void => {
            this.setState({ errorState: newErrorState });
        },
        updateSelectedProduct: (
            newSelectedProduct: Promise<SimpleProduct | null>,
            newInventory: IProductInventoryInformation | undefined,
            newPrice: ProductPrice | undefined,
            newDeliveryOptions: ProductDeliveryOptions | undefined
        ): void => {
            this.setState({
                selectedProduct: newSelectedProduct,
                productAvailableQuantity: newInventory,
                productDeliveryOptions: newDeliveryOptions
            });
            this._updatePrice(newPrice);
        },
        initializeDimension: (initializedDimension: string | undefined): void => {
            if (initializedDimension) {
                this.initializedDimensions.add(initializedDimension);
            }
        },
        dimensionSelectedAsync: async (selectedDimensionId: number, selectedDimensionValueId: string): Promise<void> => {
            this.dimensions[selectedDimensionId] = selectedDimensionValueId;
            // Only trigger _updateDimensions when all dimensions are initialized.
            if (this.initializedDimensions.size === (this.props.data.product.result?.Dimensions?.length || 0)) {
                return this.dimensionUpdateQueue
                    .enqueue(async () => {
                        return this._updateDimensions();
                    })
                    .catch((error: any) => {
                        // Ignore discarded processes.
                        if (error !== FinitePromiseQueueError.ProcessWasDiscardedFromTheQueue) {
                            throw error;
                        }
                    });
            }
        },
        getDropdownName: (dimensionType: number, resources: ILdsBuyboxResources): string => {
            return this._getDropdownName(dimensionType, resources);
        },
        changeModalOpen: (isModalOpen: boolean): void => {
            this.setState({ modalOpen: isModalOpen });
        },
        changeUpdatingDimension: (isUpdatingDimension: boolean): void => {
            this.setState({ isUpdatingDimension });
        },

        /**
         * Update isUpdatingDeliveryOptions state.
         *
         * @param isUpdatingDeliveryOptions - The status of updating delivery options.
         */
        changeUpdatingDeliveryOptions: (isUpdatingDeliveryOptions: boolean): void => {
            this.setState({ isUpdatingDeliveryOptions });
        },

        updateKeyInPrice: (customPrice: number): void => {
            // Remove custom amount error when updating the custom price
            const errorState = { ...this.state.errorState };
            errorState.customAmountError = undefined;

            this.setState({ isPriceKeyedIn: true, keyInPriceAmount: customPrice, errorState });
            this._updatePrice(this.state.productPrice, customPrice);
        },

        addSubscriptionAccountNumberAttribute: async (product: SimpleProduct, cart: ICartState) => {
            if (this.state.subscriptionAccountNumber) {
                const lineId = cart?.cart?.CartLines?.find(x =>
                    // @ts-ignore
                    x.ItemId === product.ItemId && x.AttributeValues ? x.AttributeValues[0].TextValue === 'Not Applicable' : ''
                )?.LineId;
                if (lineId) {
                    await updateCartLineAttributeValues(
                        lineId,
                        'SubscriptionAccountNumber',
                        this.state.subscriptionAccountNumber,
                        this.props.context.actionContext,
                        cart.cart
                    );
                }
            }
        }
    };

    private readonly telemetryContent: ITelemetryContent;

    constructor(props: ILdsBuyboxProps<ILdsBuyboxData>, state: IBuyboxStateExtended) {
        super(props);
        this.state = {
            errorState: {
                configureErrors: {}
            },
            quantity: 1,
            min: undefined,
            max: undefined,
            selectedProduct: undefined,
            productPrice: undefined,
            productDeliveryOptions: undefined,

            modalOpen: false,
            isUpdatingDimension: false,
            isUpdatingDeliveryOptions: false,
            showAddToCart: true,
            showFindStore: true,

            subscriptionAccountNumber: undefined,
            localCurrency: false,
            countryRegion: '',
            active: false,
            quantityError: false,
            quantityErrorMessage: '',
            pickupActive: false,
            updatedDimensions: []
        };
        this.telemetryContent = getTelemetryObject(props.context.request.telemetryPageName!, props.friendlyName, props.telemetry);
    }
    public async componentDidMount(): Promise<void> {
        const {
            data: {
                product: { result: product },
                productPrice
            }
        } = this.props;

        void productPrice.then(async result => {
            this._updatePrice(result);
        });

        if (product) {
            // Check if the product is service or not by product type
            if (product.ItemTypeValue === ReleasedProductType.Service) {
                this.setState({ isServiceItem: true });
            }
            await this._updateQuantitiesInState(product);
        }

        if (
            this.props.data.cart &&
            this.props.data.cart.result &&
            this.props.data.cart.result.cart &&
            this.props.data.cart.result.cart.AmountDue &&
            this.props.data.cart.result.cart.AmountDue > 0 &&
            this.props.data.cart.result.cart.DeliveryMode !== '' &&
            this.props.data.cart.result.cart.DeliveryMode !== 'MAG'
        ) {
            this.setState({ showAddToCart: false });
        }
    }

    public render(): JSX.Element | null {
        const {
            slots: { mediaGallery },
            data: {
                product: { result: product }
            },
            config: { className = '' }
        } = this.props;

        const { max } = this.state;

        const setValidationError = (status: boolean) => {
            this.setState({ active: status });
        };
        const setQuantityError = (status: boolean, message?: string) => {
            this.setState({ quantityError: status, quantityErrorMessage: message });
        };

        if (!product) {
            this.props.context.telemetry.error('Product content is empty, module wont render');
            return null;
        }

        const props = this.props as IBuyboxExtentedProps<IBuyboxCommonData>;
        // const props = this.props as any;
        const defaultMinimumKeyInPrice = 10;
        const defaultMaximumKeyInPrice = 100;

        const context = this.props.context as ICoreContext<IDimensionsApp>;
        const inventoryLevel = context.app.config.inventoryLevel;
        const dimensionValuesWithInventory = ArrayExtensions.flatten(
            ArrayExtensions.validValues(this.props.data.productDimensions.result?.map(value => value.dimensionValuesWithInventory))
        );
        const hasAvailableProducts =
            !ArrayExtensions.hasElements(dimensionValuesWithInventory) ||
            dimensionValuesWithInventory.some(value => {
                const inventoryLevelCode = getInventoryLevelCodeFromDimensionValue(value, inventoryLevel);
                return inventoryLevelCode !== InventoryLevelValues.outOfStock;
            });
        // const churchRetailChannelCountry = churchRetailChannelCountryAsyncAsync(
        //     this.props.context.actionContext,
        //     this.props.context.actionContext.requestContext.apiSettings.channelId
        // ).result;

        const isSubscription = product?.ExtensionProperties?.find(extProp => extProp.Key === 'isSubscription')?.Value?.BooleanValue;
        const isManualSubscription = product?.ExtensionProperties?.find(extProp => extProp.Key === 'isManualSubscription')?.Value
            ?.BooleanValue;

        const hideButton = (buttonToHide: string) => {
            if (buttonToHide === 'addToCart') {
                this.setState({ showAddToCart: true });
            } else if (buttonToHide === 'findStore') {
                this.setState({ showFindStore: true });
            }
        };

        const { productSpecificationData } = this.props.data;
        const viewOnly = productSpecificationData.result
            ? productSpecificationData.result.filter((attribute: any) => {
                  return attribute.ExtensionProperties.find((y: any) => y.Key === 'AttributeType' && y.Value.IntegerValue === 9);
              })
            : '';
        const viewProps: IBuyboxViewProps = {
            ...(this.props as ILdsBuyboxProps<ILdsBuyboxData>),
            state: this.state,
            mediaGallery: mediaGallery && mediaGallery.length > 0 ? mediaGallery[0] : undefined,
            ModuleProps: {
                moduleProps: this.props,
                className: classnames('ms-buybox', className)
            },
            ProductInfoContainerProps: {
                className: 'ms-buybox__content'
            },
            MediaGalleryContainerProps: {
                className: 'ms-buybox__media-gallery'
            },
            telemetryContent: this.telemetryContent,
            callbacks: this.buyboxCallbacks,
            title: getBuyboxProductTitle(props),
            description: getBuyboxProductDescription(props),
            bulkPurchaseLink: this._renderBuyboxBulkPurchaseLink(),
            unitOfMeasure: getBuyboxProductUnitOfMeasure(props),
            configure: getBuyboxProductConfigure(props, this.state, this.buyboxCallbacks, {
                productDimensions: this.state.updatedDimensions
            }),
            findInStore:
                this.state.showFindStore && viewOnly && viewOnly[0] && viewOnly[0].TextValue === 'ViewOnly_YES'
                    ? null
                    : this.state.showFindStore
                    ? getBuyboxFindInStore(
                          this.props,
                          this.state,
                          this.buyboxCallbacks,
                          hideButton,
                          this.state.showAddToCart,
                          productSpecificationData.result,
                          setValidationError,
                          setQuantityError,
                          max,
                          this.state.quantityErrorMessage
                      )
                    : null,
            price: getBuyboxProductPrice(props, this.state),
            // addToCart:
            //     this.state.showAddToCart && viewOnly && viewOnly[0] && viewOnly[0].TextValue === 'ViewOnly_YES'
            //         ? null
            //         : this.state.showAddToCart
            //         ? getBuyboxAddToCart(
            //               props,
            //               this.state,
            //               this.buyboxCallbacks,
            //               defaultMinimumKeyInPrice,
            //               defaultMaximumKeyInPrice,
            //               undefined,
            //               hasAvailableProducts,
            //               hideButton,
            //               productSpecificationData.result,
            //               mouseEnter,
            //               mouseLeave
            //           )
            //         : null,
            addToCart:
                viewOnly && viewOnly[0] && viewOnly[0].TextValue === 'ViewOnly_YES'
                    ? null
                    : getBuyboxAddToCart(
                          props,
                          this.state,
                          this.buyboxCallbacks,
                          defaultMinimumKeyInPrice,
                          defaultMaximumKeyInPrice,
                          undefined,
                          hasAvailableProducts,
                          hideButton,
                          productSpecificationData.result,
                          setQuantityError
                          //   mouseEnter,
                          //   mouseLeave
                      ),
            addToOrderTemplate: getBuyboxProductAddToOrderTemplate(props, this.state, this.buyboxCallbacks),
            addToWishlist: getBuyboxProductAddToWishlist(props, this.state, this.buyboxCallbacks),
            rating: !props.context.app.config.hideRating && getBuyboxProductRating(props),
            quantity:
                product.IsGiftCard || (viewOnly && viewOnly[0] && viewOnly[0].TextValue === 'ViewOnly_YES')
                    ? undefined
                    : getBuyboxProductQuantity(props, this.state, this.buyboxCallbacks),
            inventoryLabel:
                viewOnly && viewOnly[0] && viewOnly[0].TextValue === 'ViewOnly_YES' ? (
                    <div
                        style={{
                            fontSize: '16px !important',
                            backgroundColor: '#007DA5 !important',
                            marginTop: '5px !important',
                            padding: '10px !important',
                            color: 'white'
                        }}
                    >
                        {/* @ts-ignore */}
                        {props.resources.viewOnlyInventoryLabel}
                    </div>
                ) : (
                    getBuyBoxInventoryLabel(props)
                ),
            shopSimilarLook:
                this.props.config.enableShopSimilarLooks && !product.IsGiftCard
                    ? getBuyboxShopSimilarButton(props, ShopSimiliarButtonType.Looks)
                    : undefined,
            shopSimilarDescription:
                this.props.config.enableShopSimilarDescription && !product.IsGiftCard
                    ? getBuyboxShopSimilarButton(props, ShopSimiliarButtonType.Description)
                    : undefined,
            keyInPrice:
                this.props.config.enableKeyInPrice && this.state.isCustomPriceSelected
                    ? getBuyboxKeyInPrice(props, this.state, this.buyboxCallbacks)
                    : undefined,
            quantityLimitsMessages: getQuantityLimitsMessages(props, this.state),
            max,
            ProductSpecification: {
                moduleProps: this.props,
                className: classnames('ms-product-specification', className)
            },
            ProductSpecificationTableProps: {
                className: classnames('ms-product-specification__table table-striped table-bordered'),
                tag: 'table'
            },
            ProductSpecificationTableBodyProps: {
                className: '',
                tag: 'tbody'
            },
            productSpecificationResult: productSpecificationData.result,
            isSubscription: isSubscription || false,
            isManualSubscription: isManualSubscription || false,
            setSubscriptionAccountNumber: this.setSubscriptionAccountNumber,
            // churchRetailChannelCountry: churchRetailChannelCountry
            buyboxQuantityError: this.state.quantityError ? (
                // <Modal open={state.quantityError}>
                <Error style={{ marginTop: '5px' }} onClose={() => setQuantityError(false)}>
                    <div style={{ marginTop: '3px' }}>
                        {this.state.quantityErrorMessage
                            ? this.state.quantityErrorMessage
                            : format(this.props.resources.maxQuantityError, max)}
                    </div>
                </Error>
            ) : // </Modal>
            null
        };

        return this.props.renderView(viewProps) as React.ReactElement;
    }

    private readonly setSubscriptionAccountNumber = (subAccNumber: string) => {
        this.setState({ subscriptionAccountNumber: subAccNumber });
    };

    /**
     * Handles anchor click event and scrolls to the given bulk purchase module.
     * @param bulkPurchaseModule - Bulk purchase module.
     * @returns Handler.
     */
    private readonly _onBulkPurchaseLinkClick = (bulkPurchaseModule: Element) => () => {
        const boundingRect = bulkPurchaseModule.getBoundingClientRect();
        window.scrollTo(boundingRect.left, boundingRect.top);
    };

    private _renderBuyboxBulkPurchaseLink(): React.ReactNode {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access -- Temporary switch for the feature which is under development.
        if (!this.props.context.app.config.isBulkPurchaseEnabled) {
            return null;
        }

        if (!msdyn365Commerce.isBrowser) {
            return null;
        }

        const bulkPurchaseModules = Array.from(document.getElementsByClassName(BulkPurchase.className));

        if (!ArrayExtensions.hasElements(bulkPurchaseModules)) {
            return null;
        }

        return (
            <Button
                className='ms-buybox__bulk-purchase-anchor'
                title={this.props.resources.bulkPurchaseLinkText}
                onClick={this._onBulkPurchaseLinkClick(bulkPurchaseModules[0])}
            >
                {this.props.resources.bulkPurchaseLinkText}
            </Button>
        );
    }
    private _updatePrice(newPrice: ProductPrice | undefined, customPrice: number | undefined = this.state.keyInPriceAmount): void {
        if (this.state.isCustomPriceSelected && newPrice) {
            newPrice.CustomerContextualPrice = customPrice;
        }
        this.setState({ productPrice: newPrice });
    }

    private readonly _updateDimensions = async (): Promise<void> => {
        const {
            data: {
                product: { result: product }
            },
            context: {
                actionContext,
                request: {
                    apiSettings: { channelId }
                }
            }
        } = this.props;

        const productDimensions = this.props.data.productDimensions.result;

        const hasProductDimensions = ArrayExtensions.hasElements(productDimensions);

        if (!product || !hasProductDimensions) {
            return;
        }

        const dimensionsToUpdate: { [id: number]: string } = { ...this.dimensions };
        this.setState({ isUpdatingDimension: true, isUpdatingDeliveryOptions: true });

        try {
            // Step 1: Clear error state to display relevant errors
            if (this.state.errorState.otherError || this.state.errorState.quantityError) {
                const clearErrorState = { ...this.state.errorState };
                clearErrorState.otherError = undefined;
                if (this.state.errorState.errorHost === 'ADDTOCART') {
                    clearErrorState.quantityError = undefined;
                    clearErrorState.errorHost = undefined;
                }
                this.setState({ errorState: clearErrorState });
            }

            // Step 2: Clear any errors indicating the dimension wasn't selected
            for (const key of Object.keys(dimensionsToUpdate)) {
                if (this.state.errorState.configureErrors[key]) {
                    const errorState = { ...this.state.errorState };
                    errorState.configureErrors[key] = undefined;

                    this.setState({ errorState });
                }
            }

            const variantProduct: SimpleProduct | null = await this._updateProductDimensionsWithAvailabilities(dimensionsToUpdate);

            if (variantProduct) {
                // Step 5. Use these dimensions hydrate the inventory. Wrap this in a promise
                // so that places like add to cart can await it
                const newAvailableQuantity = await getProductAvailabilitiesForSelectedVariant(
                    new ProductAvailabilitiesForSelectedVariantInput(variantProduct.RecordId, channelId),
                    actionContext
                );

                const newShippingQuantity = newAvailableQuantity?.find(shipping => shipping.deliveryType === DeliveryMode.shipping);
                const isCustompriceSelected = variantProduct.Dimensions?.find(
                    dimension =>
                        dimension.DimensionTypeValue === 4 &&
                        dimension.DimensionValue &&
                        dimension.DimensionValue.Value?.toLowerCase() === 'custom'
                );
                if (isCustompriceSelected) {
                    this.setState({ isCustomPriceSelected: true });
                } else {
                    // Remove custom amount error when unselect the custom amount
                    const errorState = { ...this.state.errorState };
                    errorState.customAmountError = undefined;

                    this.setState({ isCustomPriceSelected: false, isPriceKeyedIn: false, errorState });
                }

                if (newShippingQuantity) {
                    this.setState({ productAvailableQuantity: newShippingQuantity });
                } else {
                    this.setState({ productAvailableQuantity: undefined });
                }

                // Step 6. Use these dimensions hydrate the product price.
                const newPrice = await getPriceForSelectedVariant(
                    new PriceForSelectedVariantInput(variantProduct.RecordId, channelId),
                    actionContext
                );

                if (newPrice) {
                    this._updatePrice(newPrice);
                }

                const retailMultiplePickupFeatureState = this.props.data.featureState.result?.find(
                    featureState => featureState.Name === 'Dynamics.AX.Application.RetailMultiplePickupDeliveryModeFeature'
                );

                // Step 7. Use these dimensions hydrate the product delivery options.
                const newDeliveryOptions = await getDeliveryOptionsForSelectedVariant(
                    new GetDeliveryOptionsForSelectedVariantInput(
                        variantProduct.RecordId,
                        channelId,
                        undefined,
                        undefined,
                        retailMultiplePickupFeatureState?.IsEnabled
                    ),
                    actionContext
                );

                if (newDeliveryOptions) {
                    this.setState({ productDeliveryOptions: newDeliveryOptions });
                }

                await this._updateQuantitiesInState(variantProduct);
            }
        } finally {
            this.setState({ isUpdatingDimension: false, isUpdatingDeliveryOptions: false });
        }
    };

    private async _updateProductDimensionsWithAvailabilities(dimensionsToUpdate: { [id: number]: string }): Promise<SimpleProduct | null> {
        const {
            data: {
                product: { result: product }
            },
            context: {
                actionContext,
                request: {
                    apiSettings: { channelId }
                }
            }
        } = this.props;

        const productDimensions = this.props.data.productDimensions.result!;

        // Step 3, Build the actually selected dimensions, prioritizing the information in state
        // over the information in data
        const mappedDimensions = productDimensions
            .map(dimension => {
                const dimensions = (dimension.dimensionValuesWithInventory ??
                    dimension.DimensionValues ??
                    []) as IDimensionValueForSelectedVariant[];
                return {
                    DimensionTypeValue: dimension.DimensionTypeValue,
                    DimensionValue:
                        dimensions.find(
                            value => value.DimensionValue?.RecordId.toString() === dimensionsToUpdate[dimension.DimensionTypeValue]
                        )?.DimensionValue ?? dimension.DimensionValue,
                    ExtensionProperties: dimension.ExtensionProperties
                };
            })
            .filter(dimension => dimension.DimensionValue);
        setDimensionValuesToQuery(this.props.context.actionContext.requestContext.url.requestUrl, mappedDimensions);

        // Step 4. Use these dimensions hydrate the product. Wrap this in a promise
        // so that places like add to cart can await it
        const selectedProductPromise = getSelectedVariant(
            new SelectedVariantInput(product!.MasterProductId ? product!.MasterProductId : product!.RecordId, channelId),
            actionContext
        );
        this.setState({ selectedProduct: selectedProductPromise });

        const variantProduct = await selectedProductPromise;
        let updatedDimensions;
        if (variantProduct) {
            updatedDimensions = await getDimensionsForSelectedVariant(
                new GetDimensionsForSelectedVariantInput(
                    variantProduct.MasterProductId ? variantProduct.MasterProductId : variantProduct.RecordId,
                    channelId,
                    mappedDimensions
                ),
                actionContext
            );
        }

        //LDS DEV NOTE: updates to send to configureDropdowns to load updated results for dropdowns on products after options are selected
        //using updatedDimensions to send updated list to configureDropdowns

        this.setState({ updatedDimensions: updatedDimensions });

        return variantProduct;
    }

    private async _updateQuantitiesInState(product: SimpleProduct): Promise<void> {
        const isOrderQuantityLimitsFeatureEnabled = await this._isOrderQuantityLimitsFeatureEnabled();
        if (isOrderQuantityLimitsFeatureEnabled && product) {
            this.setState({
                // quantity: product.Behavior?.DefaultQuantity || 1,
                // quantity: this.state.quantity,
                min: product.Behavior?.MinimumQuantity || 1,

                // If max by feature in default order settings is not defined then use max from site settings or default max 10.
                max:
                    product.Behavior?.MaximumQuantity && product.Behavior?.MaximumQuantity > 0
                        ? product.Behavior?.MaximumQuantity
                        : this.props.context.app.config.maxQuantityForCartLineItem || 10
            });
        } else {
            this.setState({
                min: 1,
                max: this.props.context.app.config.maxQuantityForCartLineItem || 10
            });
        }
    }

    private readonly _getDropdownName = (dimensionType: number, resources: ILdsBuyboxResources): string => {
        const isGiftCard = this.props.data.product.result?.IsGiftCard;

        switch (dimensionType) {
            case 1: // ProductDimensionType.Color
                return resources.productDimensionTypeColor;
            case 2: // ProductDimensionType.Configuration
                return resources.productDimensionTypeConfiguration;
            case 3: // ProductDimensionType.Size
                return resources.productDimensionTypeSize;
            case 4: // ProductDimensionType.Style
                return isGiftCard ? resources.productDimensionTypeAmount : resources.productDimensionTypeStyle;
            default:
                return '';
        }
    };

    private async _isOrderQuantityLimitsFeatureEnabled(): Promise<boolean> {
        const defaultOrderQuantityLimitsFeatureConfig = this.props.context?.request?.app?.platform?.enableDefaultOrderQuantityLimits;
        if (defaultOrderQuantityLimitsFeatureConfig === 'none') {
            return false;
        }

        const featureStatuses = await this.props.data.featureState;
        const isFeatureEnabledInHq = featureStatuses?.find(
            featureState => featureState.Name === RetailDefaultOrderQuantityLimitsFeatureName
        )?.IsEnabled;
        if (!isFeatureEnabledInHq) {
            return false;
        }

        if (defaultOrderQuantityLimitsFeatureConfig === 'all') {
            return true;
        }
        let customerInfo;
        try {
            customerInfo = await this.props.data.customerInformation;
        } catch (error) {
            //@ts-ignore
            this.props.telemetry.information(error);
            this.props.telemetry.debug('Unable to receive Customer Information. May be user is not authorized');
            return false;
        }

        return (
            customerInfo &&
            ((defaultOrderQuantityLimitsFeatureConfig === 'b2b' && customerInfo.IsB2b) ||
                (defaultOrderQuantityLimitsFeatureConfig === 'b2c' && !customerInfo.IsB2b))
        );
    }
}

export default Buybox;
